// Lazily fetches a location's Recurly subscription data the first time it
// becomes the active/selected location, and caches it on that location's
// slice entry (isLoaded) so switching back to an already-visited location in
// the dropdown never refetches. Uses the same per-location endpoint the old
// account-page order flow already calls (accounts/5/subscriptions?thryvId=),
// unlike the Proposal Tool's email-based orders/validate-email lookup — every
// location here already has its own thryvId from the account page.
import {useEffect, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useAuthClient} from '../../../../../utils/use-auth-client'
import {
  selectActiveLocation,
  setRecurlyAccountInfoForLocation,
} from '../../../../../app-store/PurchaseLinkSlice'
import {hasNoActiveSubscriptions} from './hasNoActiveSubscriptions'

// A brand-new account's baseline "Command Center" subscriptions (My Account
// Basic+, Reporting Center, Signatures Usage, Website Builder — the free
// items every new account gets) can still be provisioning server-side for a
// moment after account creation. A single fetch landing in that window gets
// back zero subscriptions, and since that empty result used to be cached via
// isLoaded with no retry, Order Summary would permanently show none of the
// defaults for the rest of the session. Retry a few times on an empty result
// before accepting it as final.
const EMPTY_RESULT_RETRY_DELAY_MS = 1500
const MAX_EMPTY_RESULT_RETRIES = 3

export function usePurchaseLinkLocation() {
  const dispatch = useDispatch()
  const client = useAuthClient()
  const activeLocation = useSelector(selectActiveLocation)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const retryCountRef = useRef(0)

  const locationId = activeLocation?.locationId
  const thryvId = activeLocation?.thryvId
  const isLoaded = activeLocation?.isLoaded

  useEffect(() => {
    // Parent-pay renders the combined summary beside the catalog. Its preload
    // hook can finish this same active location before this hook's duplicate
    // request does. That changes `isLoaded`, which runs this effect's cleanup
    // and cancels our request. Always clear this hook's local loader on that
    // terminal state; otherwise the cancelled request can never reach either
    // of the setLoading(false) calls below and the catalog stays locked.
    if (!locationId || !thryvId || isLoaded) {
      setLoading(false)
      return
    }

    let cancelled = false
    let retryTimer = null
    retryCountRef.current = 0
    setLoading(true)
    setError(null)

    const fetchSubscriptions = () => {
      client(`accounts/5/subscriptions?thryvId=${thryvId}`)
        .then(recurlyAccountInfo => {
          if (cancelled) return
          if (
            hasNoActiveSubscriptions(recurlyAccountInfo) &&
            retryCountRef.current < MAX_EMPTY_RESULT_RETRIES
          ) {
            retryCountRef.current += 1
            retryTimer = setTimeout(
              fetchSubscriptions,
              EMPTY_RESULT_RETRY_DELAY_MS,
            )
            return
          }
          dispatch(
            setRecurlyAccountInfoForLocation({locationId, recurlyAccountInfo}),
          )
          setLoading(false)
        })
        .catch(err => {
          if (cancelled) return
          setError(err)
          setLoading(false)
        })
    }

    fetchSubscriptions()

    return () => {
      cancelled = true
      if (retryTimer) clearTimeout(retryTimer)
    }
  }, [client, dispatch, locationId, thryvId, isLoaded])

  return {loading, error}
}
